import { useQuery } from "@tanstack/react-query";
import { useDebounceValue } from "usehooks-ts";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type {
	AddressBookHistoryDetail,
	AddressBookHistoryItem,
	AddressBookHistoryResponse,
} from "~/shared/schemas/address-book-history-schema.ts";
import { addressBookHistorySchema } from "~/shared/schemas/address-book-history-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

export type UnifiedHistoryItem = {
	validFrom: string;
	createdByInternalName?: string | null;
	createdByInternalEmail?: string | null;
	isAR?: boolean;
	isLTL?: boolean;
	changes: string;
};

export const getHistoryDate = (timestamp: string): Date => {
	if (!timestamp) return new Date(0);

	// If the timestamp doesn't have a timezone indicator, append 'Z' to treat it as UTC
	const utcTimestamp =
		timestamp.includes("Z") || /[-+]\d{2}:?\d{2}$/.test(timestamp)
			? timestamp
			: `${timestamp}Z`;

	return new Date(utcTimestamp);
};

export const formatTimestamp = (timestamp: string): string => {
	if (!timestamp) return "";

	const date = getHistoryDate(timestamp);
	if (Number.isNaN(date.getTime())) return timestamp;

	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const year = date.getFullYear();
	const hours = date.getHours();
	const minutes = date.getMinutes().toString().padStart(2, "0");
	const period = hours >= 12 ? "PM" : "AM";
	const displayHours = hours % 12 || 12;

	return `${month}/${day}/${year} ${displayHours}:${minutes} ${period}`;
};

const isStringChanged = (
	s1: string | null | undefined,
	s2: string | null | undefined,
): boolean => {
	const v1 = s1?.trim() || "";
	const v2 = s2?.trim() || "";
	return v1 !== v2;
};

const areAccessorialsChanged = (
	s1: string | null | undefined,
	s2: string | null | undefined,
): boolean => {
	if (!s1 && !s2) return false;
	if (!s1 || !s2) return true;

	try {
		const a1 = JSON.parse(s1) as number[];
		const a2 = JSON.parse(s2) as number[];

		if (!Array.isArray(a1) || !Array.isArray(a2)) return s1 !== s2;
		if (a1.length !== a2.length) return true;

		const sorted1 = [...a1].sort((a, b) => a - b);
		const sorted2 = [...a2].sort((a, b) => a - b);

		return sorted1.some((val, index) => val !== sorted2[index]);
	} catch {
		return s1 !== s2;
	}
};

const getChangesString = (
	item: AddressBookHistoryItem,
	nextItem?: AddressBookHistoryItem,
): string => {
	if (!nextItem) return "Initial creation";

	const changes = [];

	if (isStringChanged(item.companyName, nextItem.companyName))
		changes.push("Company Name");
	if (isStringChanged(item.stopName, nextItem.stopName))
		changes.push("Stop Name");
	if (isStringChanged(item.address1, nextItem.address1))
		changes.push("Address 1");
	if (isStringChanged(item.address2, nextItem.address2))
		changes.push("Address 2");
	if (isStringChanged(item.city, nextItem.city)) changes.push("City");
	if (isStringChanged(item.stateProvince, nextItem.stateProvince))
		changes.push("State");
	if (isStringChanged(item.postalCode, nextItem.postalCode))
		changes.push("Postal Code");
	if (item.isValidated !== nextItem.isValidated)
		changes.push("Validation Status");

	return changes.length > 0
		? `Updated ${changes.join(", ")}`
		: "No changes detected";
};

const getDetailChangesString = (
	item: AddressBookHistoryDetail,
	nextItem?: AddressBookHistoryDetail,
): string => {
	const prefix = item.stopType === 1 ? "Pickup" : "Delivery";
	if (!nextItem) return `${prefix}: Initial creation`;

	const changes = [];

	if (isStringChanged(item.contactName, nextItem.contactName))
		changes.push(`${prefix} Contact Name`);
	if (isStringChanged(item.contactPhone, nextItem.contactPhone))
		changes.push(`${prefix} Phone`);
	if (isStringChanged(item.contactExtension, nextItem.contactExtension))
		changes.push(`${prefix} Extension`);
	if (isStringChanged(item.hoursOpen, nextItem.hoursOpen))
		changes.push(`${prefix} Hours Open`);
	if (isStringChanged(item.hoursClose, nextItem.hoursClose))
		changes.push(`${prefix} Hours Close`);
	if (isStringChanged(item.specialInstructions, nextItem.specialInstructions))
		changes.push(`${prefix} Special Instructions`);
	if (areAccessorialsChanged(item.accessorials, nextItem.accessorials))
		changes.push(`${prefix} Accessorials`);

	return changes.length > 0
		? `Updated ${changes.join(", ")}`
		: "No changes detected";
};

const getHistoryFields = (s: string) => {
	if (s.startsWith("Updated ")) {
		return s.replace("Updated ", "").split(", ");
	}
	return [s];
};

const mergeChangeStrings = (s1: string, s2: string): string => {
	if (s1 === "No changes detected") return s2;
	if (s2 === "No changes detected") return s1;

	// Special handling for Initial creation
	const isInitial1 = s1.toLowerCase().includes("initial creation");
	const isInitial2 = s2.toLowerCase().includes("initial creation");

	if (isInitial1 && isInitial2) return "Initial creation";
	if (isInitial1) return s1; // Prefer "Initial creation" over updates at the same time
	if (isInitial2) return s2;

	const fields1 = getHistoryFields(s1);
	const fields2 = getHistoryFields(s2);

	const allFields = [...new Set([...fields1, ...fields2])];

	return `Updated ${allFields.join(", ")}`;
};

export function useAddressBookHistory(addressBookItemId?: number) {
	const { isInternal } = useDeploymentType();
	const [debouncedAddressBookItemId] = useDebounceValue(addressBookItemId, 300);
	const { data: historyData, isLoading: isLoadingHistory } = useQuery({
		queryKey: ["addressBookHistory", debouncedAddressBookItemId],
		queryFn: async () => {
			if (!debouncedAddressBookItemId) return [];

			const baseRoute = isInternal
				? apiRoutes.ADDRESSBOOKHISTORY
				: apiRoutes.EXTERNALADDRESSBOOKHISTORY;
			const route = baseRoute.replace(
				":addressBookItemId",
				String(debouncedAddressBookItemId),
			);

			const result = await getAndValidate<AddressBookHistoryResponse>(
				route,
				addressBookHistorySchema,
			);

			if (!result.success) return [];

			const itemHistory: AddressBookHistoryItem[] = result.data.itemHistory;
			const detailHistory: AddressBookHistoryDetail[] =
				result.data.detailHistory;

			// Sort by date descending to ensure nextItem logic works correctly
			const sortedItemHistory = [...itemHistory].sort(
				(a, b) =>
					getHistoryDate(b.validFrom).getTime() -
					getHistoryDate(a.validFrom).getTime(),
			);

			const processedItemHistory: UnifiedHistoryItem[] = sortedItemHistory.map(
				(item, index) => {
					const nextItem = sortedItemHistory[index + 1];
					return {
						validFrom: item.validFrom,
						createdByInternalName: item.createdByInternalName,
						createdByInternalEmail: item.createdByInternalEmail,
						isAR: item.isAR,
						isLTL: item.isLTL,
						changes: getChangesString(item, nextItem),
					};
				},
			);

			const sortedDetailHistory = [...detailHistory].sort(
				(a, b) =>
					getHistoryDate(b.validFrom).getTime() -
					getHistoryDate(a.validFrom).getTime(),
			);

			const processedDetailHistory: UnifiedHistoryItem[] =
				sortedDetailHistory.map((item: AddressBookHistoryDetail, index) => {
					const nextItem = sortedDetailHistory
						.slice(index + 1)
						.find((d) => d.stopType === item.stopType);

					// Try to find the corresponding item history to get the user who made the change
					// Preference order:
					// 1. Check if the detail record has its own audit fields
					// 2. Exact match on validFrom string
					// 3. Date-based match with a 1-second tolerance
					// 4. Most recent item record at or before the detail change
					let createdByInternalName: string | null | undefined =
						item.createdByInternalName;
					let createdByInternalEmail: string | null | undefined =
						item.createdByInternalEmail;
					let isAR: boolean | undefined = item.isAR;
					let isLTL: boolean | undefined = item.isLTL;

					if (!createdByInternalName) {
						let correspondingItem: AddressBookHistoryItem | undefined =
							sortedItemHistory.find((ih) => ih.validFrom === item.validFrom);

						if (!correspondingItem) {
							const itemTime = getHistoryDate(item.validFrom).getTime();
							correspondingItem = sortedItemHistory.find((ih) => {
								const ihTime = getHistoryDate(ih.validFrom).getTime();
								return Math.abs(ihTime - itemTime) <= 1000;
							});
						}

						if (!correspondingItem) {
							const itemTime = getHistoryDate(item.validFrom).getTime();
							correspondingItem = sortedItemHistory.find(
								(ih) => getHistoryDate(ih.validFrom).getTime() <= itemTime,
							);
						}

						if (correspondingItem) {
							createdByInternalName = correspondingItem.createdByInternalName;
							createdByInternalEmail = correspondingItem.createdByInternalEmail;
							isAR = correspondingItem.isAR;
							isLTL = correspondingItem.isLTL;
						}
					}

					return {
						validFrom: item.validFrom,
						createdByInternalName: createdByInternalName ?? null,
						createdByInternalEmail: createdByInternalEmail ?? null,
						isAR: isAR ?? false,
						isLTL: isLTL ?? false,
						changes: getDetailChangesString(item, nextItem),
					};
				});

			const allItems = [...processedItemHistory, ...processedDetailHistory];
			// Sort by date descending
			allItems.sort(
				(a, b) =>
					getHistoryDate(b.validFrom).getTime() -
					getHistoryDate(a.validFrom).getTime(),
			);

			// Group by timestamp (1s tolerance) and same user to merge changes
			const grouped: UnifiedHistoryItem[] = [];
			for (const item of allItems) {
				const last = grouped.at(-1);
				const itemTime = getHistoryDate(item.validFrom).getTime();
				const lastTime = last ? getHistoryDate(last.validFrom).getTime() : 0;

				// Same user and roughly same time (within 1 second)
				if (
					last &&
					Math.abs(itemTime - lastTime) <= 1000 &&
					last.createdByInternalName === item.createdByInternalName &&
					last.createdByInternalEmail === item.createdByInternalEmail
				) {
					last.changes = mergeChangeStrings(last.changes, item.changes);
					// Use the earliest validFrom in the group for better grouping consistency
					if (itemTime < lastTime) {
						last.validFrom = item.validFrom;
					}
				} else {
					grouped.push({ ...item });
				}
			}

			// Filter out grouping artifacts that resulted in "No changes detected"
			return grouped.filter(
				(item) =>
					item.changes !== "No changes detected" || grouped.length === 1,
			);
		},
		enabled: !!debouncedAddressBookItemId,
	});

	const isDebouncingSelectionChange =
		addressBookItemId !== debouncedAddressBookItemId;

	return {
		historyData: isDebouncingSelectionChange ? [] : historyData,
		isLoadingHistory: isLoadingHistory || isDebouncingSelectionChange,
	};
}
